import { STATUS_MATCH_EVIDENCE_BASE, buildChallengeCardSubtitle, buildChallengeCardTitle } from './challengeCard';
import { PREVIEW_REQUIRED } from './constants';
import { resolveProgramDisplayName } from './programs';
import { normalizeCampaignSlug } from './slug';
import { isStatusClaimPayloadComplete } from './statusClaimValidation';
import { allStringsNonEmpty, isStatusMatchPayloadComplete } from './statusMatchValidation';
import type { ChallengeCardBackground, PayloadBuilderState, ProgramConfig } from './types';

function previewValue(value: string): string {
  return value.trim() ? value.trim() : PREVIEW_REQUIRED;
}

function previewStringArray(values: Array<string>): Array<string> {
  return allStringsNonEmpty(values) ? values.map((value) => value.trim()) : [PREVIEW_REQUIRED];
}

function previewChallengeCardBackground(background: ChallengeCardBackground): Record<string, unknown> {
  if (background.type === 'image') {
    return {
      type: 'image',
      imageId: previewValue(background.imageId ?? ''),
    };
  }
  if (background.type === 'tierGradient') {
    return { type: 'tierGradient' };
  }
  return {
    type: 'color',
    value: previewValue(background.value ?? ''),
  };
}

export function isPayloadComplete(state: PayloadBuilderState): boolean {
  if (state.mode === 'statusClaim') {
    return isStatusClaimPayloadComplete(state);
  }

  return isStatusMatchPayloadComplete(state);
}

function mapProgramForPreview(program: ProgramConfig): Record<string, unknown> {
  const programName = program.programName.trim();
  const tiers = program.tierMappings
    .filter((mapping) => mapping.sourceTier.trim() || mapping.targetSocieteTier.trim())
    .map((mapping) => ({
      sourceTier: previewValue(mapping.sourceTier),
      targetSocieteTier: previewValue(mapping.targetSocieteTier),
    }));

  return {
    name: previewValue(programName ? resolveProgramDisplayName(programName) : ''),
    logoImageId: previewValue(program.logoImageId),
    tiers: tiers.length > 0 ? tiers : [{ sourceTier: PREVIEW_REQUIRED, targetSocieteTier: PREVIEW_REQUIRED }],
  };
}

/** Builds a JSON-shaped object for preview, marking missing required values as `[required]`. */
export function buildPreviewPayload(state: PayloadBuilderState): Record<string, unknown> {
  if (state.mode === 'statusClaim') {
    return {
      campaignId: previewValue(state.campaignId),
      slug: previewValue(normalizeCampaignSlug(state.slug)),
      termsSlug: previewValue(state.termsSlug),
      hero: {
        title: previewValue(state.heroTitle),
        subtitle: previewValue(state.heroSubtitle),
        imageId: previewValue(state.heroImageId),
      },
      partner: {
        name: previewValue(state.partnerName),
        logoImageId: previewValue(state.partnerLogoImageId),
        memberStatus: 'member',
      },
      tiers: state.tiersGranted.length > 0 ? [...state.tiersGranted] : [PREVIEW_REQUIRED],
      secondarySection: {
        title: state.secondaryTitle.trim(),
        subtitle: state.secondarySubtitle.trim(),
      },
    };
  }

  const programs =
    state.programs.length > 0
      ? state.programs.map(mapProgramForPreview)
      : [
          {
            name: PREVIEW_REQUIRED,
            logoImageId: PREVIEW_REQUIRED,
            tiers: [{ sourceTier: PREVIEW_REQUIRED, targetSocieteTier: PREVIEW_REQUIRED }],
          },
        ];

  const conditions =
    state.conditions.length > 0
      ? state.conditions.map((condition) => ({
          icon: condition.icon,
          title: previewValue(condition.title),
          description: previewValue(condition.description),
        }))
      : [{ icon: 'clock', title: PREVIEW_REQUIRED, description: PREVIEW_REQUIRED }];

  const howItWorks =
    state.howItWorks.length > 0
      ? state.howItWorks.map((step) => ({
          title: previewValue(step.title),
          description: previewValue(step.description),
        }))
      : [{ title: PREVIEW_REQUIRED, description: PREVIEW_REQUIRED }];

  return {
    slug: previewValue(normalizeCampaignSlug(state.slug)),
    campaignId: previewValue(state.campaignId),
    allowedRegions:
      state.allowedRegions !== undefined && state.allowedRegions.length > 0
        ? [...state.allowedRegions]
        : [PREVIEW_REQUIRED],
    hero: {
      title: previewValue(state.heroTitle),
      subtitle: previewValue(state.heroSubtitle),
      description: previewValue(state.heroDescription),
      ctaText: previewValue(state.heroCtaText),
      imageId: previewValue(state.heroImageId),
    },
    programs,
    evidence: {
      ...STATUS_MATCH_EVIDENCE_BASE,
      minPhotos: state.evidenceMinPhotos > 0 ? state.evidenceMinPhotos : PREVIEW_REQUIRED,
      photoHints: previewStringArray(state.evidencePhotoHints),
      acceptedFormats: previewStringArray(state.evidenceAcceptedFormats),
      proofRequirements: previewStringArray(state.evidenceProofRequirements),
      verificationImageId: previewValue(state.evidenceVerificationImageId),
    },
    conditions,
    howItWorks,
    challengeCard: {
      background: previewChallengeCardBackground(state.challengeCardBackground),
      cardTitle: buildChallengeCardTitle(previewValue(state.challengeCardTitleSuffix)),
      cardSubtitle: buildChallengeCardSubtitle(
        previewValue(state.challengeCardSubtitleBefore),
        previewValue(state.challengeCardSubtitleAfter),
      ),
    },
    successSteps: state.successSteps.map((step) => ({
      key: step.key,
      title: previewValue(step.title),
      description: previewValue(step.description),
    })),
    faqs: state.faqs
      .filter((faq) => faq.question || faq.answer)
      .map((faq) => ({
        question: faq.question,
        answer: faq.answer,
      })),
    terms: state.terms.filter(Boolean),
    termsSlug: previewValue(state.termsSlug),
  };
}

export function serializePreviewPayload(state: PayloadBuilderState): string {
  return JSON.stringify(buildPreviewPayload(state), null, 2);
}
